"use client";

import { FaceLandmarker, type NormalizedLandmark } from "@mediapipe/tasks-vision";
import { useEffect, useRef, useState } from "react";
import { logger } from "@/core/logging";
import { computeObjectPosition, FACIAL_THIRDS_FALLBACK } from "./facialThirdsUtils";
import { MEDIAPIPE_FACE_LANDMARKER_MODEL_URL, prefersMediapipeCpuDelegate, resolveVisionTasksFileset } from "./mediapipeConfig";
export { FACIAL_THIRDS_FALLBACK };

/**
 * Key MediaPipe Face Mesh landmark indices used to locate each facial zone.
 * Full mesh reference: https://storage.googleapis.com/mediapipe-assets/documentation/mediapipe_face_landmark_fullsize.png
 */
const LM = {
  FOREHEAD_TOP: 10,
  // mid-forehead / hairline proxy
  LEFT_BROW_ARCH: 105,
  // left eyebrow highest point
  RIGHT_BROW_ARCH: 334,
  // right eyebrow highest point
  NOSE_TIP: 1,
  // nose tip — used for stable horizontal center
  SUBNASALE: 2,
  // base of nose / top of philtrum
  CHIN_TIP: 152 // menton
} as const;
export type FacialThirdsPositions = {
  upper: string;
  middle: string;
  lower: string;
};

/**
 * Runs one-shot MediaPipe Face Landmarker inference (IMAGE mode) on the
 * assessment photo and returns `objectPosition` strings that precisely center
 * each facial third inside a 2:1 card.
 *
 * - Starts with `FACIAL_THIRDS_FALLBACK` so the UI is never blank.
 * - Updates positions asynchronously once the model finishes (~200 ms from
 *   browser cache after the initial capture-stage WASM warm-up).
 * - Fails silently — any error keeps the fallback values.
 */
export function useFacialThirdsPositions(imageUrl: string | undefined): {
  positions: FacialThirdsPositions;
  isDetecting: boolean;
} {
  const [positions, setPositions] = useState<FacialThirdsPositions>(FACIAL_THIRDS_FALLBACK);
  const [isDetecting, setIsDetecting] = useState(false);
  const landmarkerRef = useRef<FaceLandmarker | null>(null);
  const hasRunRef = useRef(false);

  // Run detection once per mounted instance.
  useEffect(() => {
    if (!imageUrl || hasRunRef.current) return;

    // Object property instead of a plain `let` so TypeScript doesn't narrow the
    // value to `false` after each `if (ctrl.cancelled) return` guard — the
    // cleanup can mutate this between any two awaits.
    const ctrl = {
      cancelled: false
    };
    const run = async () => {
      setIsDetecting(true);
      try {
        if (!landmarkerRef.current) {
          const vision = await resolveVisionTasksFileset();
          function createThirdsLandmarker(delegate: "GPU" | "CPU") {
            return FaceLandmarker.createFromOptions(vision, {
              baseOptions: {
                modelAssetPath: MEDIAPIPE_FACE_LANDMARKER_MODEL_URL,
                delegate
              },
              runningMode: "IMAGE",
              numFaces: 1,
              minFaceDetectionConfidence: 0.5,
              minFacePresenceConfidence: 0.5,
              minTrackingConfidence: 0.5,
              outputFaceBlendshapes: false,
              outputFacialTransformationMatrixes: false
            });
          }
          if (prefersMediapipeCpuDelegate()) {
            landmarkerRef.current = await createThirdsLandmarker("CPU");
          } else {
            try {
              landmarkerRef.current = await createThirdsLandmarker("GPU");
            } catch (gpuErr) {
              logger.warn("facial_thirds_landmarker_gpu_failed_falling_back_to_cpu", {
                error: gpuErr instanceof Error ? gpuErr.message : String(gpuErr)
              });
              landmarkerRef.current = await createThirdsLandmarker("CPU");
            }
          }
        }
        if (ctrl.cancelled) return;
        const img = await new Promise<HTMLImageElement>((resolve, reject) => {
          const el = new Image();
          el.crossOrigin = "anonymous";
          el.onload = () => resolve(el);
          el.onerror = () => reject(new Error("image_load_failed"));
          el.src = imageUrl;
        });

        // The cleanup can set ctrl.cancelled between any two awaits; TypeScript
        // can't model external mutation so the rule is suppressed intentionally.
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (ctrl.cancelled) return;
        const result = landmarkerRef.current.detect(img);
        if (!result.faceLandmarks.length) return;
        const lm: NormalizedLandmark[] = result.faceLandmarks[0];
        const foreheadY = lm[LM.FOREHEAD_TOP].y;
        const browY = (lm[LM.LEFT_BROW_ARCH].y + lm[LM.RIGHT_BROW_ARCH].y) / 2;
        const noseBaseY = lm[LM.SUBNASALE].y;
        const chinY = lm[LM.CHIN_TIP].y;
        const faceX = lm[LM.NOSE_TIP].x;

        // detect() is synchronous — ctrl.cancelled cannot change between here and
        // line 95. React 18 safely ignores setState on unmounted components, so
        // we can always write state without a guard.
        hasRunRef.current = true;
        setPositions({
          upper: computeObjectPosition((foreheadY + browY) / 2, faceX),
          middle: computeObjectPosition((browY + noseBaseY) / 2, faceX),
          lower: computeObjectPosition((noseBaseY + chinY) / 2, faceX)
        });
      } catch (err) {
        logger.error("facial_thirds_landmark_detection_failed", undefined, err as Error);
        // Stay on fallback — no UI disruption.
      } finally {
        setIsDetecting(false);
      }
    };
    void run();
    return () => {
      ctrl.cancelled = true;
      // Dispose the landmarker when the effect tears down (imageUrl change or unmount).
      if (landmarkerRef.current) {
        landmarkerRef.current.close();
        landmarkerRef.current = null;
      }
    };
  }, [imageUrl]);
  return {
    positions,
    isDetecting
  };
}